// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import FilterAutocomplete from "components/FilterAutocomplete";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/MUI";

// Data
import axios from "axios";
import { withAuthContext } from "context/Auth";
import { useEffect, useState, useCallback, useMemo } from "react";
import MDBadge from "components/MDBadge";
import AdminIcon from "assets/images/bg-profile.jpeg";
import { useNavigate } from "react-router-dom";
import ImageCloud from "../../link";
import { useCappedRetry } from "utils/retry";

function Tables({ Token, CheckToken, currAdmin, GetCurrentAdmin, AdminRole, Task, AssignTask }) {
	const navigate = useNavigate();
	const retryGetAdmins = useCappedRetry();
	const [data, setData] = useState([]);
	const [error, setError] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, totalPages: 0 });
	const [currentPage, setCurrentPage] = useState(1);
	const [itemsPerPage, setItemsPerPage] = useState(10);
	const [filters, setFilters] = useState({ role: "", towerNo: "", isArchived: "" });
	const [filterOptions, setFilterOptions] = useState({ roles: [], towerNos: [], archiveStatuses: [] });
	const [isFilterLoading, setIsFilterLoading] = useState(false);
	const [filterError, setFilterError] = useState("");

	const roleOptions = useMemo(() =>
		(filterOptions?.roles || []).map((role) => ({
			label: role,
			value: role
		})),
	[filterOptions?.roles]
	);

	const towerOptions = useMemo(() =>
		(filterOptions?.towerNos || []).map((tower) => ({
			label: `Tower ${tower}`,
			value: String(tower)
		})),
	[filterOptions?.towerNos]
	);

	const archiveOptions = useMemo(() =>
		(filterOptions?.archiveStatuses || []).map((state) => ({
			label: state ? "Archived" : "Active",
			value: String(state)
		})),
	[filterOptions?.archiveStatuses]
	);

	const columns = useMemo(
		() => [
			{
				headerName: "",
				field: "profilePicture",
				flex: 1,
				renderCell: (params) => (
					<img
						src={
							params?.row?.profilePicture?.filename
								? `${ImageCloud}/${params?.row?.profilePicture?.filename}`
								: AdminIcon
						}
						style={{
							width: "50px",
							height: "50px",
							borderRadius: "50px",
							border: "1px solid #000"
						}}
					/>
				)
			},
			{ headerName: "name", field: "name", flex: 1 },
			{ headerName: "email", field: "email", flex: 1 },
			{ headerName: "phone Number", field: "phoneNumber", flex: 1 },
			{ headerName: "Role", field: "Role", flex: 1 },
			{ headerName: "TowerNo", field: "TowerNo", flex: 1 },
			{ headerName: "isArchived", field: "isArchived", flex: 1 },
			{
				headerName: "Action",
				field: "action",
				flex: 1,
				renderCell: (params) => (
					<MDBox>
						<MDBadge
							badgeContent={Task ? "Assign Admin" : "Change Admin Tower"}
							color="dark"
							variant="gradient"
							size="sm"
							onClick={() => {
								if (Task) {
									AssignTask({ assignedTo: params?.row?._id });
								} else {
									navigate(`/AdminAction/${params?.row?._id}`);
								}
							}}
						/>
					</MDBox>
				)
			}
		],
		[Task, AssignTask, navigate]
	);

	const GetAdmins = useCallback(() => {
		if (Token || localStorage.getItem("token")) {
			setIsLoading(true);
			const params = {
				page: currentPage,
				limit: itemsPerPage
			};
			if (filters.role) params.role = filters.role;
			if (filters.towerNo) params.towerNo = filters.towerNo;
			if (filters.isArchived !== "") params.isArchived = filters.isArchived;

			axios
				.get(
					`${process.env.REACT_APP_PUBLIC_PATH}/${Task ? `GetAllAdminTask/${Task}` : "GetAllAdmins"}`,
					{
						params,
						headers: {
							Authorization: `Bearer ${Token || localStorage.getItem("accessToken") || localStorage.getItem("token")}`
						}
					}
				)
				.then(async (res) => {
					if (res?.data?.status == 200) {
						let arr = await res?.data?.data.filter((a) => a?._id !== currAdmin);
						if (Task) {
							arr = await arr.filter((a) => a?.Role != "superadmin");
							if (AdminRole == "manager") {
								arr = await arr.filter((a) => a?.Role != "admin");
							}
						}
						setData(arr);
						if (res?.data?.pagination) {
							setPagination(res?.data?.pagination);
						}
					} else {
						setError(res?.data?.message);
					}
				})
				.catch((err) => {
					setError(err?.message);
				})
				.finally(() => {
					setIsLoading(false);
				});
		} else {
			retryGetAdmins(() => {
				CheckToken();
				GetAdmins();
			});
		}
	}, [Token, Task, currentPage, itemsPerPage, filters, currAdmin, AdminRole, CheckToken]);

	const handleFilterChange = useCallback((field, value) => {
		setCurrentPage(1);
		setFilters((prev) => ({
			...prev,
			[field]: value || ""
		}));
	}, []);

	const handleClearFilters = useCallback(() => {
		setCurrentPage(1);
		setFilters({ role: "", towerNo: "", isArchived: "" });
	}, []);

	const fetchAdminFilters = useCallback(() => {
		const tokenHeader = Token || localStorage.getItem("accessToken") || localStorage.getItem("token");
		if (!tokenHeader) return;
		setIsFilterLoading(true);
		setFilterError("");
		axios
			.get(`${process.env.REACT_APP_PUBLIC_PATH}/GetAdminFilters`, {
				headers: { Authorization: `Bearer ${tokenHeader}` }
			})
			.then((res) => {
				if (res?.data?.status === 200) {
					setFilterOptions(res?.data?.data || {});
				} else {
					setFilterError(res?.data?.message || "Unable to load filters");
				}
			})
			.catch((err) => {
				setFilterError(err?.response?.data?.message || err?.message || "Unable to load filters");
			})
			.finally(() => setIsFilterLoading(false));
	}, [Token]);

	useEffect(() => {
		fetchAdminFilters();
	}, [fetchAdminFilters]);

	useEffect(() => {
		GetCurrentAdmin();
	}, []);

	useEffect(() => {
		GetAdmins();
	}, [GetAdmins]);

	const handlePageChange = useCallback((newPage, newPageSize) => {
		if (newPageSize !== itemsPerPage) {
			setItemsPerPage(newPageSize);
			setCurrentPage(1);
		} else {
			setCurrentPage(newPage);
		}
	}, [itemsPerPage]);

	// ✅ Memoized TableComponent
	const TableComponent = useMemo(
		() => (
			<>
				<MDBox px={2} py={2} mt={2} bgcolor="rgba(255,255,255,0.8)" borderRadius="md">
					<Grid container spacing={2} alignItems="center">
						<Grid item xs={12} sm={6} md={4}>
							<FilterAutocomplete
								name="role"
								label="Role"
								value={filters.role}
								options={roleOptions}
								onChange={handleFilterChange}
								loading={isFilterLoading}
								disabled={isFilterLoading}
								placeholder="Search role"
							/>
						</Grid>
						<Grid item xs={12} sm={6} md={4}>
							<FilterAutocomplete
								name="towerNo"
								label="Tower"
								value={filters.towerNo}
								options={towerOptions}
								onChange={handleFilterChange}
								loading={isFilterLoading}
								disabled={isFilterLoading}
								placeholder="Search tower"
							/>
						</Grid>
						<Grid item xs={12} sm={6} md={4}>
							<FilterAutocomplete
								name="isArchived"
								label="Archived"
								value={filters.isArchived}
								options={archiveOptions}
								onChange={handleFilterChange}
								loading={isFilterLoading}
								disabled={isFilterLoading}
								placeholder="Search state"
							/>
						</Grid>
						<Grid item xs={12} display="flex" justifyContent="flex-end">
							<MDButton
								variant="outlined"
								color="info"
								size="small"
								disabled={isFilterLoading}
								onClick={handleClearFilters}
							>
								Clear filters
							</MDButton>
						</Grid>
					</Grid>
					{filterError && (
						<MDTypography variant="caption" color="error">
							{filterError}
						</MDTypography>
					)}
				</MDBox>
				<MDBox pt={3}>
					{isLoading ? (
						<MDBox lineHeight={1} textAlign="center" py={3}>
							<MDTypography display="block" variant="caption" color="text" fontWeight="medium" fontSize="16px">
								Loading...
							</MDTypography>
						</MDBox>
					) : data.length > 0 ? (
						<DataTable 
							rows={data} 
							columns={columns} 
							paginationMode="server"
							serverPagination={{
								total: pagination?.total || 0,
								page: currentPage,
								limit: itemsPerPage,
								onPageChange: handlePageChange
							}}
						/>
					) : (
						<MDBox lineHeight={1} textAlign="center">
							<MDTypography
								display="block"
								variant="caption"
								color="text"
								fontWeight="medium"
								fontSize="20px"
							>
								{error ? error : " No Data Found"}
							</MDTypography>
						</MDBox>
					)}
				</MDBox>
			</>
		),
		[data, columns, error, isLoading, pagination, currentPage, itemsPerPage, handlePageChange, filters, filterOptions, isFilterLoading, filterError, handleFilterChange, handleClearFilters]
	);

	if (Task) {
		return TableComponent;
	} else {
		return (
			<DashboardLayout>
				<DashboardNavbar />
				<MDBox pt={6} pb={3}>
					<Grid container spacing={6}>
						<Grid item xs={12}>
							<Card>
								<MDBox
									mx={2}
									mt={-3}
									py={3}
									px={2}
									variant="gradient"
									bgColor="info"
									borderRadius="lg"
									coloredShadow="info"
								>
									<Grid container style={{ width: "100%" }}>
										<Grid item xs={6}>
											<MDTypography variant="h6" color="white">
												Admin
											</MDTypography>
										</Grid>
										{AdminRole == "superadmin" && (
											<Grid
												item
												xs={6}
												style={{
													display: "flex",
													alignItems: "flex-end",
													alignContent: "flex-end",
													justifyContent: "flex-end"
												}}
											>
												<MDBadge
													badgeContent="New"
													color="dark"
													variant="gradient"
													size="sm"
													onClick={() => navigate(`/AdminAction/New`)}
												/>
											</Grid>
										)}
									</Grid>
								</MDBox>

								{TableComponent}
							</Card>
						</Grid>
					</Grid>
				</MDBox>
			</DashboardLayout>
		);
	}
}


export default withAuthContext(Tables);
